// RUTA: src/hooks/useClientProfile.ts

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { UserProfile, HistoryEntry, Supplement, RoutineDay, Meal } from '@/types/fitness';
// PERFILES legacy hardcoded data has been removed; the dynamic flow
// from the database is the single source of truth for every user.
// NOTA: NO importar componentes de lucide-react para asignarlos a
// objetos del UserProfile — el perfil se serializa a localStorage y
// los componentes React no son JSON-serializables. Causaron el
// crash IFT-011 / IFT-012 (React #130). Los iconos se resuelven
// en render time dentro de los componentes que los muestran.
import { setCachedData, getCachedData, cachedQuery } from '@/lib/offlineStorage';
import { resetCleanReloadAttempts } from '@/lib/cleanReload';
import { getTodayLocalString } from '@/lib/dateUtils';

const profileCacheKey = (uid: string) => `client_profile_${uid}`;
const activeUserCacheKey = (uid: string) => `client_active_user_${uid}`;
const isLegacyCacheKey = (uid: string) => `client_is_legacy_${uid}`;

// Labels para modo tiempo
const TIME_LABELS: Record<string, string> = {
  breakfast: '🌅 Desayuno',
  lunch: '🍗 Comida',
  dinner: '🌙 Cena',
  snack1: '🥜 Colación',
  snack2: '🍎 Snack',
};

// Labels para modo número
function getNumberLabel(mealTime: string): string {
  const match = mealTime.match(/^meal_(\d+)$/);
  if (match) return `🍽️ Comida ${match[1]}`;
  return TIME_LABELS[mealTime] || mealTime;
}

export function useClientProfile(overrideUserId?: string | null) {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activeUser, setActiveUser] = useState<string>('');
  const [isLegacy, setIsLegacy] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchDynamicProfile = useCallback(async (userId: string) => {
    try {
      // ── Parallel fetch ───────────────────────────────────────────
      // All queries below depend ONLY on userId — they have no
      // sequential dependency on each other. Running them in parallel
      // makes total wait = max(latencies) instead of sum.
      //
      // On cellular this drops profile-load wall time from ~6-8s
      // (8 sequential RTTs) to ~1-2s (1 RTT). Without this fix, any
      // single slow query stacked the total above the 25s threshold
      // that triggers the StuckLoader recovery panel.
      //
      // dayTitles is fetched alongside the others; it's applied AFTER
      // routinesData is processed but doesn't need to wait for it
      // network-wise.
      const [
        onboardingRes,
        profileRowRes,
        goalsRes,
        measurementsRes,
        routinesRes,
        dayTitlesRes,
        mealRes,
        suppRes,
      ] = await Promise.all([
        cachedQuery<any>(`onboarding_${userId}`, () =>
          supabase.from('client_onboarding_profiles').select('*').eq('user_id', userId).maybeSingle()
        ),
        cachedQuery<any>(`profile_row_${userId}`, () =>
          supabase.from('profiles').select('display_name, figure_type').eq('id', userId).single()
        ),
        cachedQuery<any>(`goals_${userId}`, () =>
          supabase.from('client_goals').select('*').eq('client_id', userId).maybeSingle()
        ),
        cachedQuery<any[]>(`measurements_${userId}`, () =>
          supabase.from('body_measurements').select('*').eq('user_id', userId).order('measure_date', { ascending: true })
        ),
        cachedQuery<any[]>(`routines_${userId}`, () =>
          supabase.from('client_routines').select('*').eq('client_id', userId).eq('is_active', true).order('day_name').order('exercise_order', { ascending: true })
        ),
        cachedQuery<any[]>(`day_titles_${userId}`, () =>
          supabase.from('client_routine_day_titles').select('day_name, title').eq('client_id', userId)
        ),
        cachedQuery<any[]>(`meals_${userId}_week`, () =>
          supabase.from('client_meal_plans')
            .select('*')
            .eq('client_id', userId)
            .eq('is_active', true)
            .order('meal_order', { ascending: true })
        ),
        cachedQuery<any[]>(`supplements_${userId}`, () =>
          supabase.from('supplements').select('*').eq('user_id', userId).order('hora_especifica')
        ),
      ]);

      const onboarding = onboardingRes.data;
      const profileRow = profileRowRes.data;
      const goals = goalsRes.data;
      const measurements = measurementsRes.data;
      const routinesData = routinesRes.data;
      const dayTitles = dayTitlesRes.data;
      const mealData = mealRes.data;
      const suppData = suppRes.data;

      const displayName = profileRow?.display_name || onboarding?.full_name || 'Cliente';

      const historial: HistoryEntry[] = (measurements || []).map((row: any) => ({
        sesion: row.session_label,
        fecha: formatDate(row.measure_date),
        peso: Number(row.peso) || 0,
        grasa: Number(row.grasa) || 0,
        imc: row.imc != null ? Number(row.imc) : undefined,
        masaLibreGrasa: row.masa_libre_grasa != null ? Number(row.masa_libre_grasa) : undefined,
        agua: row.agua != null ? Number(row.agua) : undefined,
        grasaVisceral: row.grasa_visceral != null ? Number(row.grasa_visceral) : undefined,
        metabolismoBasal: row.metabolismo_basal != null ? Number(row.metabolismo_basal) : undefined,
        masaMuscular: row.masa_muscular != null ? Number(row.masa_muscular) : undefined,
      }));

      if (historial.length === 0) {
        historial.push({
          sesion: 'Baseline',
          fecha: formatDate(onboarding?.created_at?.split('T')[0] || getTodayLocalString()),
          peso: Number(onboarding?.weight_kg) || 0,
          grasa: Number(onboarding?.body_fat_pct) || 0,
          imc: onboarding?.bmi != null ? Number(onboarding.bmi) : undefined,
        });
      }

      let rutina: RoutineDay[] | undefined;
      if (routinesData && routinesData.length > 0) {
        const dayMap = new Map<string, RoutineDay>();
        for (const r of routinesData as any[]) {
          if (!dayMap.has(r.day_name)) {
            dayMap.set(r.day_name, {
              dia: r.day_name,
              grupo: r.muscle_group || '',
              cardio: '',
              ejercicios: [],
            });
          }
          const day = dayMap.get(r.day_name)!;
          if (r.muscle_group && !day.grupo.includes(r.muscle_group)) {
            day.grupo = day.grupo ? `${day.grupo} + ${r.muscle_group}` : r.muscle_group;
          }
          day.ejercicios.push({
            nombre: r.exercise_name,
            series: r.sets ? Number(r.sets) : undefined,
            reps: r.reps || undefined,
            isometria: (r.tempo_eccentrica || r.tempo_concentrica)
              ? `${r.tempo_eccentrica || 0}-${r.tempo_pausa_abajo || 0}-${r.tempo_concentrica || 0}-${r.tempo_pausa_arriba || 0}`
              : r.isometry_seconds ? `${r.isometry_seconds}s` : undefined,
            pesoCoach: r.weight_spec || (r.suggested_weight ? `${r.suggested_weight}` : '--'),
            pesoReal: '--',
            comentarios: r.technical_notes || undefined,
            equipmentType: r.equipment_type || undefined,
            weightSpec: r.weight_spec || undefined,
            restOptions: r.rest_options || [],
            tempoEccentrica: r.tempo_eccentrica || 0,
            tempoPausaAbajo: r.tempo_pausa_abajo || 0,
            tempoConcentrica: r.tempo_concentrica || 0,
            tempoPausaArriba: r.tempo_pausa_arriba || 0,
            demoMediaUrl: r.demo_media_url || undefined,
            demoMediaType: r.demo_media_type || undefined,
            imageUrl: r.image_url || undefined,
          });
        }
        const DAYS_ORDER = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
        rutina = Array.from(dayMap.values()).sort(
          (a, b) => DAYS_ORDER.indexOf(a.dia) - DAYS_ORDER.indexOf(b.dia)
        );

        // Override con títulos manuales que el coach puso por día. Si hay
        // fila en `client_routine_day_titles` ese texto manda; si no, se
        // queda el auto ("Glúteo + Cuádriceps + ..."). dayTitles ya se
        // cargó en el Promise.all de arriba — solo aplicamos.
        if (dayTitles && dayTitles.length > 0) {
          const titleByDay = new Map(dayTitles.map((t: any) => [t.day_name, t.title]));
          rutina = rutina.map(d =>
            titleByDay.has(d.dia) ? { ...d, grupo: titleByDay.get(d.dia)! } : d
          );
        }
      }

      // ✅ Meals — fetch the WHOLE week so the client can switch days in
      // the meal-plan section (same UX as RoutineSection day chips).
      // Plans imported via Nutrium have one row per (day_of_week, meal_time);
      // plans without day_of_week (NULL) apply to every day so they show
      // up regardless of which day the user is viewing.
      // mealData ya se cargó en el Promise.all de arriba.
      let planAlimenticio: Meal[] | undefined;
      let planMode: 'time' | 'number' = 'time';

      if (mealData && (mealData as any[]).length > 0) {
        // Detectar modo del plan desde el primer registro
        const firstMeal = (mealData as any[])[0];
        if (firstMeal?.plan_mode) {
          planMode = firstMeal.plan_mode;
        } else if (firstMeal?.meal_time?.startsWith('meal_')) {
          planMode = 'number';
        }

        // Agrupar por (day_of_week, meal_time) so per-day variation
        // (Nutrium-style plans) keeps Lunes·Desayuno separate from
        // Martes·Desayuno.  meal_time alone collides across days.
        const mealMap = new Map<string, Meal>();
        for (const m of mealData as any[]) {
          const dayOfWeek = m.day_of_week || null;
          const key = `${dayOfWeek ?? 'all'}::${m.meal_time}`;
          if (!mealMap.has(key)) {
            const label = planMode === 'number' ? getNumberLabel(m.meal_time) : (TIME_LABELS[m.meal_time] || m.meal_time);
            mealMap.set(key, {
              id: m.id,
              nombre: m.meal_time,
              label,
              items: [],
              rawText: m.food_item,
              mealOrder: m.meal_order ?? 0,
              planMode,
              coachTime: m.coach_time || undefined,
              clientTime: m.client_time || undefined,
              completedDate: m.completed_date || undefined,
              specialInstructions: m.special_instructions || undefined,
              dayOfWeek,
            });
          }
          // Convertir texto libre a items (una línea = un item)
          const lines = (m.food_item || '').split('\n').map((l: string) => l.trim()).filter(Boolean);
          mealMap.get(key)!.items.push(...lines);
        }
        planAlimenticio = Array.from(mealMap.values()).sort((a, b) => (a.mealOrder ?? 0) - (b.mealOrder ?? 0));
      }

      // suppData ya se cargó en el Promise.all de arriba.
      // OJO: NO incluir un componente React (ej. icono: Pill) aquí.
      // Este objeto se serializa a localStorage; las funciones se
      // pierden y al deserializar quedan como `{}`, lo cual rompe el
      // render con React #130. El ícono se renderiza directamente
      // en SupplementsSection.
      const suplementos: Supplement[] = ((suppData || []) as any[]).map((s: any) => ({
        hora: s.horario,
        nombre: s.nombre,
        dosis: s.descripcion,
      }));

      const metaCalorias = goals?.daily_calories
        ? Number(goals.daily_calories)
        : onboarding?.adjusted_daily_calories
          ? Number(onboarding.adjusted_daily_calories)
          : 2000;

      const metaGrasa = goals?.target_body_fat
        ? Number(goals.target_body_fat)
        : onboarding?.target_body_fat_pct
          ? Number(onboarding.target_body_fat_pct)
          : 20;

      const edad = onboarding?.date_of_birth
        ? calculateAge(onboarding.date_of_birth)
        : 0;

      const primaryGoal = onboarding?.primary_goal || 'Fitness';
      const goalName = `${displayName.split(' ')[0].toUpperCase()} - ${primaryGoal.toUpperCase()}`;

      const userProfile: UserProfile = {
        edad,
        metaGrasa,
        metaCalorias,
        nombreMeta: goalName,
        enfoque: primaryGoal,
        color: '#dc2626',
        historial,
        suplementos,
        rutina,
        planAlimenticio,
        planMode,
        coachNotes: goals?.coach_notes || undefined,
        nextEvaluationDate: goals?.next_evaluation_date || undefined,
        clientStatus: goals?.client_status || undefined,
        metaCardioKcal: goals?.daily_cardio_kcal ? Number(goals.daily_cardio_kcal) : undefined,
      };

      setCachedData(profileCacheKey(userId), userProfile);
      setCachedData(activeUserCacheKey(userId), displayName || 'Cliente');
      setCachedData(isLegacyCacheKey(userId), false);

      setActiveUser(displayName || 'Cliente');
      setProfile(userProfile);
      setIsLegacy(false);

      // El perfil cargó exitosamente desde la red. Si el usuario
      // estaba en medio de intentos de "Recarga limpia" (URL tiene
      // ?_clr=N), los reseteamos — el problema que tenía ya se
      // resolvió. Esto evita que un cliente que tuvo problemas hace
      // tiempo se quede con el contador alto y no pueda usar la
      // recarga limpia más adelante.
      resetCleanReloadAttempts();
    } catch (error) {
      console.error('Error fetching dynamic profile:', error);
      const cached = getCachedData<UserProfile>(profileCacheKey(userId));
      const cachedUser = getCachedData<string>(activeUserCacheKey(userId));
      if (cached) {
        setProfile(cached);
        setActiveUser(cachedUser || 'Cliente');
        setIsLegacy(false);
      }
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setLoading(false);
      return;
    }

    const init = async () => {
      // ── Stale-while-revalidate ───────────────────────────────────
      // If we already have a cached profile from a previous visit,
      // paint it INSTANTLY (loading=false) and refetch in the
      // background. This makes refreshes feel native: the UI
      // appears in <100ms instead of waiting 1-8s for the network.
      //
      // The user only ever sees the StuckLoader on the very first
      // visit (no cache yet) or after clearing browser data.
      // Single code path for everyone: load profile from the database.
      // PERFILES (legacy hardcoded data) was removed — every user, including
      // the original ones (Joel, Alexa), now uses this dynamic flow.
      // Security is enforced by Supabase RLS on every queried table.
      const targetUserId = overrideUserId || user.id;
      const cached = getCachedData<UserProfile>(profileCacheKey(targetUserId));
      const cachedUser = getCachedData<string>(activeUserCacheKey(targetUserId));

      if (cached) {
        setProfile(cached);
        setActiveUser(cachedUser || 'Cliente');
        setIsLegacy(false);
        setLoading(false);
        // Refresh in the background; setProfile inside fetchDynamicProfile
        // will replace this with fresh data when network completes.
        fetchDynamicProfile(targetUserId).catch(() => {
          // Network errors are silent — we already painted cached data.
        });
        return;
      }

      // No cache → must wait for network (first visit / cleared cache).
      try {
        await fetchDynamicProfile(targetUserId);
      } catch {
        // Last-resort: even with no cache and a failed fetch we don't
        // crash. The user will see the empty state in Index.tsx.
        setIsLegacy(false);
      }
      setLoading(false);
    };

    init();
  }, [user, authLoading, fetchDynamicProfile, overrideUserId]);

  // Refetch del perfil cuando vuelve la conexión sin tener que cerrar
  // y reabrir la app. Antes el usuario tenía que hacer reload manual
  // para que los datos del servidor reemplazaran el caché viejo.
  useEffect(() => {
    if (!user) return;
    const onReconnected = () => {
      const targetId = overrideUserId || user.id;
      fetchDynamicProfile(targetId).catch(() => {
        // El error ya se loggea adentro; no romper la UI.
      });
    };
    window.addEventListener('iftmx:reconnected', onReconnected);
    return () => window.removeEventListener('iftmx:reconnected', onReconnected);
  }, [user, overrideUserId, fetchDynamicProfile]);

  const refetchProfile = useCallback(() => {
    const targetId = overrideUserId || user?.id;
    if (targetId) return fetchDynamicProfile(targetId);
    return Promise.resolve();
  }, [overrideUserId, user, fetchDynamicProfile]);

  const effectiveUserId = overrideUserId || user?.id;

  return { profile, activeUser, isLegacy, loading, refetchProfile, userId: user?.id, effectiveUserId };
}

function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

function calculateAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}
